/**
 * A work area's pages as a tree (F-0201).
 *
 * A page names its parent with `parentPageId`, and `position` orders it among that parent's
 * children. Home is the work area's root: it is always first and never has a parent, and nothing
 * ever sits under it — the pages below Home are the tree, and Home is not one of its nodes.
 *
 * Everything here is pure and platform-agnostic: the server uses it to refuse a cycle or an
 * over-deep move, the sidebar uses it to draw the tree, and the CLI uses it to print one.
 */

/**
 * How deep a page may sit, counting the page itself: a top-level page is level 1, and
 * `MAX_PAGE_TREE_DEPTH` is the deepest level any page may reach.
 *
 * The bound is a reading bound, not a storage one. Each level costs one indent step
 * (`PAGE_TREE_INDENT_STEP_PX`) out of a sidebar that is 224px wide, and a level-5 title starts
 * 60px further in than Home's with about 140px left for its text before it truncates. Six levels
 * would leave a title unreadable rather than merely short, so five is where it stops.
 */
export const MAX_PAGE_TREE_DEPTH = 5;

/**
 * The indent one level costs in the sidebar and the phone menu, in pixels: one small step, with no
 * guide line (I-1332, Jay: "dont show a guide line just indent. keep it visually clean"). No row has
 * a caret column — a row with children draws its chevron in its icon's place — so a child's icon
 * stands exactly this far right of its parent's.
 */
export const PAGE_TREE_INDENT_STEP_PX = 12;

/** The shape this module needs from a page. The full page (`IWorkAreaDashboardPage`) satisfies it. */
export interface IPageTreeNodeLike {
  id: string;
  isHome: boolean;
  parentPageId: string | null;
  position: number;
  title: string;
}

export interface IPageTreeNode<TPage extends IPageTreeNodeLike> {
  page: TPage;
  /** 1 for a top-level page, 2 for its child, and so on. */
  level: number;
  children: IPageTreeNode<TPage>[];
}

/**
 * The pages below Home as a tree, keeping the order the server gave at every level (P-4: nothing
 * re-sorts here).
 *
 * A page whose parent is not in the list — deleted, or outside this work area — is drawn at the top
 * level rather than dropped, so a page is never invisible because of a row that is not there.
 */
export function buildPageTree<TPage extends IPageTreeNodeLike>(
  pages: readonly TPage[],
): IPageTreeNode<TPage>[] {
  const custom = pages.filter((page) => !page.isHome);
  const present = new Set(custom.map((page) => page.id));
  const childrenOf = new Map<string, TPage[]>();
  const roots: TPage[] = [];
  for (const page of custom) {
    const parentId = page.parentPageId;
    if (parentId && present.has(parentId) && parentId !== page.id) {
      const siblings = childrenOf.get(parentId);
      if (siblings) siblings.push(page);
      else childrenOf.set(parentId, [page]);
    } else {
      roots.push(page);
    }
  }

  // `seen` stops a cycle the database should never hold from hanging the sidebar: a page already
  // drawn on this path is not drawn again below itself.
  const seen = new Set<string>();
  const build = (page: TPage, level: number): IPageTreeNode<TPage> => {
    seen.add(page.id);
    const children = (childrenOf.get(page.id) ?? [])
      .filter((child) => !seen.has(child.id))
      .map((child) => build(child, level + 1));
    return { page, level, children };
  };
  const nodes = roots.filter((page) => !seen.has(page.id)).map((page) => build(page, 1));
  // A cycle has no root at all, so nothing above would have drawn it. Whatever the walk never
  // reached is drawn at the top level: a page hidden by a broken parent is worse than a flat one.
  for (const page of custom) {
    if (!seen.has(page.id)) nodes.push(build(page, 1));
  }
  return nodes;
}

/** Every page in the tree, parents before their children, exactly as the tree reads top to bottom. */
export function flattenPageTree<TPage extends IPageTreeNodeLike>(
  nodes: readonly IPageTreeNode<TPage>[],
): IPageTreeNode<TPage>[] {
  return nodes.flatMap((node) => [node, ...flattenPageTree(node.children)]);
}

/**
 * The ids of a page's ancestors, nearest parent first. A page whose parent is missing has none, and
 * a cycle stops at the page it comes back to rather than looping.
 */
export function pageAncestorIds<TPage extends IPageTreeNodeLike>(
  pages: readonly TPage[],
  pageId: string,
): string[] {
  const byId = new Map(pages.map((page) => [page.id, page]));
  const ancestors: string[] = [];
  const seen = new Set<string>([pageId]);
  let current = byId.get(pageId)?.parentPageId ?? null;
  while (current && !seen.has(current) && byId.has(current)) {
    ancestors.push(current);
    seen.add(current);
    current = byId.get(current)?.parentPageId ?? null;
  }
  return ancestors;
}

/** A page's level, counting itself: 1 at the top level. */
export function pageLevel<TPage extends IPageTreeNodeLike>(
  pages: readonly TPage[],
  pageId: string,
): number {
  return pageAncestorIds(pages, pageId).length + 1;
}

/** The ids of everything under a page, the page itself excluded. */
export function pageDescendantIds<TPage extends IPageTreeNodeLike>(
  pages: readonly TPage[],
  pageId: string,
): string[] {
  const childrenOf = new Map<string, string[]>();
  for (const page of pages) {
    if (!page.parentPageId) continue;
    const siblings = childrenOf.get(page.parentPageId);
    if (siblings) siblings.push(page.id);
    else childrenOf.set(page.parentPageId, [page.id]);
  }
  const found: string[] = [];
  const seen = new Set<string>([pageId]);
  const queue = [...(childrenOf.get(pageId) ?? [])];
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    found.push(id);
    queue.push(...(childrenOf.get(id) ?? []));
  }
  return found;
}

/** How many levels a page's own subtree is, counting the page itself: 1 when it has no children. */
export function pageSubtreeHeight<TPage extends IPageTreeNodeLike>(
  pages: readonly TPage[],
  pageId: string,
): number {
  const byId = new Map(pages.map((page) => [page.id, page]));
  const descendants = pageDescendantIds(pages, pageId);
  let height = 1;
  for (const id of descendants) {
    let depth = 1;
    const seen = new Set<string>([id]);
    let current = byId.get(id)?.parentPageId ?? null;
    while (current && current !== pageId && !seen.has(current)) {
      seen.add(current);
      depth += 1;
      current = byId.get(current)?.parentPageId ?? null;
    }
    height = Math.max(height, depth + 1);
  }
  return height;
}

/**
 * Who is looking at a page: the permissions they hold in the work area (what their roles grant by
 * default), the pages shared with them or with a role they hold, and whether they may read the work
 * area's pages at all (`work_area.read`) and manage them (`work_area.manage`).
 */
export interface IPageReader {
  heldPermissions: readonly string[];
  /** The ids of the pages shared with this reader, directly or through a role they hold. */
  sharedPageIds: ReadonlySet<string>;
  mayReadWorkArea: boolean;
  mayManage: boolean;
}

/**
 * Whether a page is shown to a reader (T-0783, AGM-5, AGM-32). **Presentation, never a grant**
 * (§21.2 of `concept-design-agency-production-and-automation.md`): this decides whether the page is
 * in the reader's sidebar and may be opened, and nothing about what a block on it may read.
 *
 * - A manager who may read the work area sees every page, so a limited page can still be edited.
 * - A page shared with the reader (a user, or a role they hold) is shown: a share is a permission on
 *   that page only, caused by the share and removed with it.
 * - A reader who may not read the work area (a guest) sees nothing else.
 * - Otherwise a page that names no `audiencePermission` is shown to everyone who may read the work
 *   area, and one that names a permission is shown to the readers whose roles grant it.
 */
export function pageIsShownTo(
  page: { id: string; audiencePermission?: string | null },
  reader: IPageReader,
): boolean {
  if (reader.mayReadWorkArea && reader.mayManage) return true;
  if (reader.sharedPageIds.has(page.id)) return true;
  // A guest (no `work_area.read`) sees only what is shared with them or a role they hold. A
  // permission a page names never shows it to them: every guest role holds `work_item.read_shared`,
  // so a tester shared one page would otherwise see the customer's.
  if (!reader.mayReadWorkArea) return false;
  if (!page.audiencePermission) return true;
  return reader.heldPermissions.includes(page.audiencePermission);
}
